import { DOCUMENT, inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '@environments/environment';
import { CurrentLanguageService } from '@shared/services/language';
import { LanguageAvailable, LANGUAGES } from '@shared/services/language/registry';
import { LogsService } from '@shared/services/logs';
import { isRelAllowedMultipleTimes } from '@shared/services/seo/config';
import { JsonLD } from '@shared/services/seo/models';
import { envConfiguredHostname } from '@shared/utils';

// SEO decision on DIGSOC-5183: every version of a content names the same x-default, its first language in this list.
const X_DEFAULT_LANGUAGES: readonly LanguageAvailable[] = ['fr-FR', 'es-ES', 'fr-BE'];

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private readonly twitterCardList = new Set<string>();
  private readonly openGraphList = new Set<string>();
  private readonly jsonLdScriptList = new Set<string>();
  private readonly titleService = inject(Title);
  private readonly metaService = inject(Meta);
  private readonly logs = inject(LogsService);
  private readonly renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
  private readonly document = inject(DOCUMENT);
  private readonly language = inject(CurrentLanguageService).language;

  /**
   * Updates the document title if it differs from the current title.
   * This method ensures that the title is updated only when necessary
   * to prevent redundant changes to the title state.
   *
   * @param title - The new title to be set for the document. It is a string
   * representing the desired document title.
   * @returns void
   * @example
   * ```typescript
   * readonly seoService = inject(SeoService);
   * this.seoService.updateTitle('Welcome to MyApp');
   * // If the current title is not 'Welcome to MyApp', it will be updated.
   * ```
   * @remarks
   * This method internally uses a title service (`titleService`) to handle the
   * current title state and update functionality. Ensure that the title service
   * is properly initialized before calling this method.
   */
  updateTitle(title: string) {
    if (title !== this.titleService.getTitle()) {
      this.titleService.setTitle(title);
    }
  }

  /**
   * Updates the meta description tag in the document's metadata.
   *
   * If a non-empty `description` is provided, the method updates or creates the
   * meta tag with the given description. If `description` is an empty string,
   * the existing meta description tag is removed.
   *
   * @param description - The new content for the meta description tag. If an
   * empty string (default value) is provided, the meta description tag will be removed.
   * @returns {void} This method does not return any value.
   *
   * @example
   * // Update the meta description to "Welcome to the site".
   * updateMetaDescription("Welcome to the site");
   *
   * // Remove the meta description.
   * updateMetaDescription("");
   *
   * @remarks
   * This method relies on an internal `metaService` to manage the document's
   * metadata. Ensure that `metaService` is correctly configured for the method
   * to function as expected.
   */
  updateMetaDescription(description: string = ''): void {
    if (description.length > 0) {
      this.metaService.updateTag({ name: 'description', content: description });
    } else {
      this.metaService.removeTag('name="description"');
    }
  }

  /**
   * Updates or sets the canonical link `<link rel="canonical">` for the current document.
   * This method constructs the canonical URL using the provided `pathname` and the environment-configured hostname.
   *
   * @param {string} pathname - The specific path to append to the hostname for creating the canonical URL.
   *                            If an empty string is provided, only the hostname will be used.
   * @returns {void} This method does not return a value.
   *
   * @example
   * // Assuming 'envConfiguredHostname' returns 'https://example.com'
   *
   * readonly seoService = inject(SeoService);
   *
   * // Sets the canonical link to 'https://example.com/about'
   * this.seoService.setCanonicalLink('about');
   *
   * // Sets the canonical link to 'https://example.com' (no path)
   * this.seoService.setCanonicalLink('');
   *
   * @remarks
   * This method internally calls a private function `setLinkTag` to manipulate the DOM and insert or update
   * the `<link rel="canonical">` element. Ensure that `envConfiguredHostname` provides a valid hostname.
   */
  setCanonicalLink(pathname: string): void {
    const hostname = envConfiguredHostname(this.language.region);
    const url = pathname.length > 0 ? `${hostname}/${pathname}` : hostname;
    this.setLinkTag('canonical', url);
  }

  /**
   * Adds a preconnect link tag to the document to facilitate early connection
   * to the specified URL, improving loading performance for critical external resources.
   *
   * @param {string} url - The URL to preconnect to. It must be a valid, absolute URL.
   * @returns {void} This method does not return a value.
   *
   * @example
   * // Preconnect to a third-party API to enhance performance
   * setPreconnectLink('https://api.example.com');
   *
   * @remarks
   * Preconnect allows the browser to perform DNS resolution, TLS negotiations,
   * and other connection setup processes before the resource is needed,
   * reducing latency during the resource's usage.
   */
  setPreconnectLink(url: string): void {
    this.setLinkTag('preconnect', url);
  }

  /**
   * Replaces the hreflang alternates of the page with one per translation of `hrefLang` (Drupal langcode to path)
   * that the environment publishes, on the domain of its language, plus `x-default` towards its version in the first
   * of `X_DEFAULT_LANGUAGES` it has. A page with fewer than two published versions gets none.
   */
  setAlternateLinks(hrefLang: Record<string, string>): void {
    for (const link of Array.from(this.document.head.querySelectorAll('link[rel="alternate"][hreflang]'))) {
      this.renderer.removeChild(this.document.head, link);
    }
    const alternates = Object.entries(hrefLang).flatMap(([langcode, path]) => {
      const language = Object.values(LANGUAGES).find((candidate) => candidate.langcode === langcode);
      if (!language || !environment.domains[language.region].publishedLanguages.includes(language.locale)) return [];
      // The CMS answers a homepage as `/homepage`, `/es/homepage` or `/es/`: the root of its language.
      const root = language.urlPrefix ? `/${language.urlPrefix}` : '';
      const pathname = path.replace(/\/+$/, '');
      const href =
        envConfiguredHostname(language.region) + ((pathname === `${root}/homepage` ? root : pathname) || '/');
      return [{ hreflang: language.locale, href }];
    });
    if (alternates.length < 2) return;
    const xDefaultLocale = X_DEFAULT_LANGUAGES.find((locale) => alternates.some(({ hreflang }) => hreflang === locale));
    const xDefault = alternates.find(({ hreflang }) => hreflang === xDefaultLocale);
    const links = xDefault ? [...alternates, { hreflang: 'x-default', href: xDefault.href }] : alternates;
    for (const { hreflang, href } of links) {
      const link = this.renderer.createElement('link') as HTMLLinkElement;
      this.renderer.setAttribute(link, 'rel', 'alternate');
      this.renderer.setAttribute(link, 'hreflang', hreflang);
      this.renderer.setAttribute(link, 'href', href);
      this.renderer.appendChild(this.document.head, link);
    }
  }

  /**
   * Configures and updates Open Graph meta tags for a web page.
   *
   * This method removes any obsolete Open Graph meta tags that are no longer relevant
   * based on the provided properties and ensures the supplied Open Graph properties
   * are added or updated. It modifies the internal state and interacts with the meta
   * tag management service to reflect the changes dynamically.
   *
   * @param {Record<string, string>} [properties={}] - A record where keys are Open Graph
   * property names (excluding the "og:" prefix) and values are the corresponding content
   * to be set. Defaults to an empty object, which will remove all existing Open Graph tags
   * without adding new ones.
   *
   * @returns {void} This method does not return a value.
   *
   * @example
   * // Add or update Open Graph tags for a page.
   * setOpenGraph({
   *   title: "Amazing Web Page",
   *   image: "https://example.com/image.jpg",
   *   url: "https://example.com",
   * });
   *
   * @remarks
   * This method interacts with an internal meta tag management service to update the
   * document's meta tags dynamically. Ensure that the `metaService` is correctly
   * configured and accessible for updates to take effect.
   */
  setOpenGraph(properties: Record<string, string> = {}): void {
    this.removeObsoleteMetaTags('og', properties);
    Object.entries(properties).forEach(([property, content]) => {
      this.openGraphList.add(`property="og:${property}"`);
      this.metaService.updateTag({ property: `og:${property}`, content });
    });
  }

  /**
   * Configures and updates Twitter Card metadata for the current page.
   * This method ensures that obsolete metadata is removed and new properties
   * are added or updated as specified.
   *
   * @param {Record<string, string>} [properties={}] - A key-value pair object representing
   * the Twitter Card metadata. Each key corresponds to a Twitter Card property (excluding the `twitter:` prefix),
   * and the value specifies the content of the corresponding meta tag.
   * Defaults to an empty object.
   *
   * @returns {void} This method does not return a value.
   *
   * @example
   * // Set Twitter Card metadata for a page
   * setTwitterCard({
   *   title: 'Example Title',
   *   description: 'An example description for the page.',
   *   image: 'https://example.com/image.jpg'
   * });
   *
   * @remarks
   * This method internally handles the removal of obsolete Twitter Card metadata tags
   * and guarantees the uniqueness of the `name="twitter:*"` meta tags.
   * It utilizes the service `metaService.updateTag` to modify meta tag content in the DOM.
   */
  setTwitterCard(properties: Record<string, string> = {}): void {
    this.removeObsoleteMetaTags('twitter', properties);
    Object.entries(properties).forEach(([name, content]) => {
      this.twitterCardList.add(`name="twitter:${name}"`);
      this.metaService.updateTag({ name: `twitter:${name}`, content });
    });
  }

  /**
   * Adds or updates a JSON-LD `<script>` element in the document's `<head>` section.
   * This method ensures a unique script element for the provided `id`, updating its content
   * if it already exists or creating a new one otherwise. The JSON-LD script is used
   * to provide structured metadata for search engines and other consumers of the page.
   *
   * @param {string} id - A unique identifier for the `<script>` element. This must be
   * a non-empty string to properly associate the JSON-LD script.
   * @param {JsonLD} content - A valid JSON-LD object containing structured data.
   * The content will be serialized using `JSON.stringify` before being added or updated
   * in the script element.
   *
   * @throws {Error} If the document `<head>` element cannot be accessed.
   *
   * @example
   * ```typescript
   * readonly seoService = inject(SeoService);
   *
   * // Define a simple JSON-LD structured data object
   * const productSchema = {
   *   "@context": "https://schema.org/",
   *   "@type": "Product",
   *   "name": "Example Product",
   *   "description": "An example product description.",
   *   "brand": {
   *     "@type": "Brand",
   *     "name": "Example Brand"
   *   }
   * };
   *
   * // Add the JSON-LD structured data to the head
   * this.seoService.addJsonLdScript('productSchema', productSchema);
   * ```
   *
   * @remarks
   * - Logs an error if the `id` is empty, invalid, or the `content` fails JSON serialization.
   * - Logs a warning if the resulting JSON-LD content is empty or invalid.
   * - This function is idempotent for a given `id`—repeated calls with the same `id` will update
   * the content of the existing script rather than duplicating it.
   */
  addJsonLdScript(id: string, content: JsonLD): void {
    this.logs.trace('SeoService', 'addJsonLdScript', { id, content });
    // Validate the input params
    if (!id?.trim()) {
      this.logs.error('SeoService', 'JSON-LD injection canceled : empty or invalid id.', { id, content });
      return;
    }

    let json: string | undefined;
    try {
      json = JSON.stringify(content);
    } catch (e) {
      this.logs.error('SeoService', `JSON.stringify failed for "${id}"`, e, content);
      return;
    }

    if (!json || json === 'undefined' || json === '{}' || json === 'null') {
      this.logs.warn('SeoService', `JSON-LD "${id}" ignored (empty or undefined).`, content);
      return;
    }

    this.jsonLdScriptList.add(id);

    // Check if a script element with the given ID already exists
    const existingScript = this.document?.querySelector<HTMLScriptElement>(`script#${id}`);

    if (existingScript) {
      // Update the existing script's content
      this.renderer.setProperty(existingScript, 'textContent', JSON.stringify(content));
    } else {
      // Create a new <script> element
      const jsonLdScript = this.renderer.createElement('script') as HTMLScriptElement;
      this.renderer.setAttribute(jsonLdScript, 'type', 'application/ld+json');
      this.renderer.setAttribute(jsonLdScript, 'id', id);
      this.renderer.setProperty(jsonLdScript, 'textContent', JSON.stringify(content));

      // Safely append the new script element to the <head>
      const headElement = this.document?.head;
      if (!headElement) {
        throw new Error('Unable to access the document head.');
      }

      this.renderer.appendChild(headElement, jsonLdScript);
    }
  }

  /**
   * Removes a JSON-LD `<script>` element from the current document's `<head>` by its ID.
   * This method locates the `<script>` element with the specified ID and safely removes it
   * from the DOM if it exists.
   *
   * @param {string} id - The ID of the `<script>` element to be removed. Must be a non-empty string.
   * @throws {Error} Throws an error if the `id` parameter is an empty string or consists only of whitespace.
   * @returns {void} This method does not return a value.
   *
   * @example
   * // Assuming the document's <head> contains a <script> element with id="json-ld-script":
   * readonly seoService = inject(SeoService);
   * this.seoService.removeJsonLdScript('json-ld-script');
   *
   * // After the method call, the <script> element is removed from the DOM.
   *
   * @remarks
   * - This method relies on a valid document and renderer context; ensure they are properly initialized before invoking it.
   * - No action is taken if the specified `<script>` element is not found, and no error is thrown in this case.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/querySelector
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Node/removeChild
   */
  removeJsonLdScript(id: string): void {
    this.logs.trace('SeoService', 'removeJsonLdScript', { id });
    // Validate the input param
    if (!id?.trim()) {
      throw new Error(`Invalid id attribute: "${id}"`);
    }

    // Locate the script element by ID
    const scriptToRemove = this.document?.querySelector<HTMLScriptElement>(`script#${id}`);

    if (scriptToRemove) {
      // Safely remove the script element
      this.renderer.removeChild(this.document?.head, scriptToRemove);
    }
  }

  /**
   * Removes a `<link>` element from the document's `<head>` with a specified `rel` attribute value.
   * If no matching `<link>` element is found, no action is taken.
   *
   * @param rel - The `rel` attribute value of the `<link>` element to remove. Must be a non-empty string.
   * @throws {Error} Throws an error if the `rel` parameter is an empty or invalid string.
   * @returns {void} This method does not return a value.
   *
   * @example
   * // Assuming a document contains: <link rel="stylesheet" href="styles.css">
   *
   * readonly seoService = inject(SeoService);
   * this.seoService.removeLinkTag('stylesheet');
   *
   * // Result: The <link rel="stylesheet"> element is removed from the document's head.
   */
  removeLinkTag(rel: string): void {
    this.logs.trace('SeoService', 'removeLinkTag', { rel });
    // Validate input to ensure it's not empty
    if (!rel?.trim()) {
      throw new Error(`Invalid rel attribute: "${rel}"`);
    }

    const linkElement = this.document?.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

    // If the link exists, remove it from the document head
    if (linkElement) {
      this.renderer.removeChild(this.document.head, linkElement);
    }
  }

  /**
   * Updates or creates a `<link>` element in the document `<head>` with the specified `rel` and `href` attributes.
   *
   * If a `<link>` element with the same `rel` attribute already exists and multiple instances of the given `rel` type are not allowed,
   * the existing element's `href` attribute will be updated. Otherwise, a new `<link>` element will be created and appended to the `<head>` element.
   *
   * @param rel A non-empty string specifying the `rel` attribute of the link element (e.g., "stylesheet", "icon").
   * @param href A non-empty string specifying the `href` attribute of the link element (e.g., a URL or path to a resource).
   * @throws {Error} If `rel` or `href` is an empty or invalid string.
   * @throws {Error} If the document's `<head>` element cannot be accessed.
   *
   * @returns {void} This method does not return a value.
   *
   * @example
   * ```typescript
   * // Add a new stylesheet link
   * setLinkTag('stylesheet', 'https://example.com/styles.css');
   *
   * // Update the existing favicon link
   * setLinkTag('icon', '/favicon.ico');
   * ```
   *
   * @remarks
   * - This method ensures no duplicate `<link>` elements are created for `rel` types that should only have a single instance.
   * - The validation ensures that invalid or empty values for `rel` and `href` will result in an error before any DOM changes are made.
   * - The method assumes that the `document` property provides access to the global document object and that the `renderer` property is an abstraction over DOM manipulation for better encapsulation and testability.
   */
  setLinkTag(rel: string, href: string): void {
    // Validate input to prevent empty or invalid values
    if (!rel?.trim()) {
      throw new Error(`Invalid rel attribute: "${rel}"`);
    }
    if (!href?.trim()) {
      throw new Error(`Invalid href attribute: "${href}"`);
    }

    // Check if a link element with the same "rel" and "href" already exists
    // This prevents adding duplicate links even if multiple links of the same "rel" are allowed
    const exactMatch = this.document?.querySelector<HTMLLinkElement>(`link[rel="${rel}"][href="${href}"]`);
    if (exactMatch) {
      return;
    }

    // Check if a link element with the same "rel" already exists
    const existingLink = this.document?.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

    if (existingLink && !isRelAllowedMultipleTimes(rel)) {
      // If link exists, update the "href" attribute
      // This avoids creating duplicate link tags
      this.renderer.setAttribute(existingLink, 'href', href);
    } else {
      // If link does not exist, create a new <link> element
      const newLink = this.renderer.createElement('link') as HTMLLinkElement;
      this.renderer.setAttribute(newLink, 'rel', rel);
      this.renderer.setAttribute(newLink, 'href', href);

      // Safely append the new link element to the <head>
      const headElement = this.document?.head;
      if (!headElement) {
        throw new Error('Unable to access the document head.');
      }

      this.renderer.appendChild(headElement, newLink);
    }
  }

  /**
   * Removes obsolete meta tags of a specific type (either "twitter" or "og") from the DOM
   * based on a new set of properties, ensuring that only up-to-date meta tags remain.
   *
   * This method compares the existing meta tags with the provided properties, determines
   * which tags are outdated, and removes them. It also clears the internal cache of
   * managed meta tags for the specified type.
   *
   * @param type - The type of meta tags to process. Must be either "twitter" for Twitter Card tags
   * or "og" for Open Graph tags.
   * @param newProperties - A record of new meta properties to compare against the existing tags.
   * Keys represent the meta property names (without the prefix), and values are ignored in this method.
   *
   * @returns {void} This function does not return a value. It performs DOM side-effects by removing tags.
   *
   * @example
   * ```typescript
   * const newTwitterProperties = {
   *   description: "Updated description",
   *   image: "https://example.com/image.jpg",
   * };
   *
   * // Remove obsolete Twitter meta tags and update cache
   * this.removeObsoleteMetaTags("twitter", newTwitterProperties);
   *
   * const newOpenGraphProperties = {
   *   title: "Updated Title",
   *   url: "https://example.com",
   * };
   *
   * // Remove obsolete Open Graph meta tags and update cache
   * this.removeObsoleteMetaTags("og", newOpenGraphProperties);
   * ```
   *
   * @remarks
   * - The method heavily relies on two internal caches, `#twitterCardList` and `#openGraphList`,
   *   to determine which tags are no longer relevant. These caches are cleared during the operation.
   * - The `getMissingProperties` private method is used to identify tags that should be removed.
   * - This method assumes that `metaService.removeTag` successfully removes the specified meta tags from the DOM.
   *
   * @see getMissingProperties This private utility method is used internally to calculate properties to remove.
   */
  private removeObsoleteMetaTags(type: 'twitter' | 'og', newProperties: Record<string, string>): void {
    const newPropertiesArray = new Set<string>();
    let propertiesToRemove: string[] = [];
    if (type === 'twitter') {
      Object.entries(newProperties).forEach(([property]) => {
        newPropertiesArray.add(`name="twitter:${property}"`);
      });
      propertiesToRemove = this.getMissingProperties(this.twitterCardList, newPropertiesArray);
      this.twitterCardList.clear();
    }
    if (type === 'og') {
      Object.entries(newProperties).forEach(([property]) => {
        newPropertiesArray.add(`property="og:${property}"`);
      });
      propertiesToRemove = this.getMissingProperties(this.openGraphList, newPropertiesArray);
      this.openGraphList.clear();
    }
    propertiesToRemove.forEach((property) => {
      this.metaService.removeTag(property);
    });
  }

  /**
   * Compares two sets of properties and returns an array of properties that are present in the first set
   * but missing from the second set.
   *
   * This method is useful for identifying discrepancies between two collections of property names,
   * such as detecting missing required attributes or configuration keys.
   *
   * @param actualProperties - A set containing the existing properties that need to be checked.
   * @param newPropertiesArray - A set containing the properties that serve as the baseline or reference.
   * @returns An array of properties (strings) that are in `actualProperties` but not in `newPropertiesArray`.
   *
   * @example
   * // Example usage:
   * const actual = new Set(['name', 'age', 'email']);
   * const reference = new Set(['name', 'email']);
   *
   * const missing = obj.getMissingProperties(actual, reference);
   * console.log(missing);
   * // Output: ['age']
   */
  private getMissingProperties(actualProperties: Set<string>, newPropertiesArray: Set<string>): string[] {
    return [...actualProperties].filter((property) => !newPropertiesArray.has(property));
  }
}
